import { SimpleGame } from "@/app";
import getDecorators from "inversify-inject-decorators";
import container from "../../../../ioc/inversify.config";
import TYPES from "../../../../ioc/types";
import { AuthenticatedAssetLoader } from "../../../../services/authenticated-asset-loader";
import { GameStateDTO } from "../../../../shared/dtos";
import { Endpoints, SceneId } from "../../../../shared/enum";
import { CreateGameMenu } from "../../../menu/title-screen/create-game-menu";
import { drawBackground } from "../../../utils/scene-background";

const { lazyInject } = getDecorators(container);

export class NewGameScene extends Phaser.Scene {
    private menu: CreateGameMenu;
    private leftArrow: Phaser.GameObjects.Triangle;
    private rightArrow: Phaser.GameObjects.Triangle;
    private pageIndicator: Phaser.GameObjects.Text;

    @lazyInject(TYPES.AuthenticatedAssetLoader)
    private assetLoader: AuthenticatedAssetLoader;

    constructor() {
        super({ key: SceneId.NEW_GAME_SCREEN });
        console.log("constructing new game scene");
    }

    init() {
        console.log("init new game scene");
        this.sys.events.on("wake", () => this.scene.bringToTop());
        this.scene.bringToTop();
    }

    public create(sceneId: string) {
        console.log("basic new game scene creation");
        drawBackground(this);

        this.loadAuthenticatedAssets()
            .then(() => {
                const saves = this.cache.json.get("game_saves") as GameStateDTO[];
                console.log("retrieved saves: ", saves);
                const screenWidth = this.physics.world.bounds.centerX;
                const screenHeight = this.physics.world.bounds.centerY;

                this.menu = new CreateGameMenu(0, 0, screenWidth, screenHeight, saves, this);
                this.add.existing(this.menu);

                this.createNavigationUI();

                this.menu.setPageChangeCallback((page, totalPages) => {
                    this.updateNavigationUI(page, totalPages);
                });
            })
            .catch((error) => {
                console.error("Failed to load game saves:", error);
            });
    }

    private createNavigationUI() {
        this.leftArrow = this.add.triangle(
            50,
            this.cameras.main.height / 2,
            0,
            0,
            20,
            -15,
            20,
            15,
            0xffffff
        );
        this.leftArrow.setInteractive({ useHandCursor: true });
        this.leftArrow.on("pointerdown", () => {
            if (this.menu.canGoToPreviousPage()) {
                this.menu.previousPage();
            }
        });
        this.leftArrow.on("pointerover", () => {
            if (this.menu.canGoToPreviousPage()) {
                this.leftArrow.setScale(1.2);
            }
        });
        this.leftArrow.on("pointerout", () => {
            this.leftArrow.setScale(1.0);
        });

        this.rightArrow = this.add.triangle(
            this.cameras.main.width - 50,
            this.cameras.main.height / 2,
            20,
            0,
            0,
            -15,
            0,
            15,
            0xffffff
        );
        this.rightArrow.setInteractive({ useHandCursor: true });
        this.rightArrow.on("pointerdown", () => {
            if (this.menu.canGoToNextPage()) {
                this.menu.nextPage();
            }
        });
        this.rightArrow.on("pointerover", () => {
            if (this.menu.canGoToNextPage()) {
                this.rightArrow.setScale(1.2);
            }
        });
        this.rightArrow.on("pointerout", () => {
            this.rightArrow.setScale(1.0);
        });

        this.pageIndicator = this.add.text(
            this.cameras.main.width / 2,
            this.cameras.main.height - 50,
            "",
            {
                fontSize: "16px",
                color: "#ffffff",
                fontFamily: "monospace",
            }
        );
        this.pageIndicator.setOrigin(0.5);
    }

    private updateNavigationUI(page: number, totalPages: number) {
        if (!this.leftArrow || !this.rightArrow || !this.pageIndicator) {
            return;
        }

        this.leftArrow.setVisible(page > 0);
        this.rightArrow.setVisible(page < totalPages - 1);

        if (totalPages > 1) {
            this.pageIndicator.setText(`Page ${page + 1} / ${totalPages}`);
            this.pageIndicator.setVisible(true);
        } else {
            this.pageIndicator.setVisible(false);
        }

        this.leftArrow.setAlpha(page > 0 ? 1.0 : 0.3);
        this.rightArrow.setAlpha(page < totalPages - 1 ? 1.0 : 0.3);
    }

    private async loadAuthenticatedAssets(): Promise<void> {
        const url = `${Endpoints.BASE_URL}/${Endpoints.GAME_STATE}/load-games/?projectId=${SimpleGame.PROJECT_ID}`;
        console.log("Url ", url);
        await this.assetLoader.loadJson(this, "game_saves", url);
    }
}
